import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sun, Moon, Menu, X, User, Shield, LogOut, Globe, Swords, CalendarDays, Ticket, GraduationCap, Phone, Settings, Check } from "lucide-react";
import { DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { TEST_IDS } from "@/constants/testIds";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import NotificationBell from "@/components/NotificationBell";
import PlayerAvatar from "@/components/PlayerAvatar";

export const LOGO_URL = "/dd-logo-v2.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, lang, setLang, languageNames, languageFlags } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: "/book", label: t("nav.book"), icon: CalendarDays, testId: "nav-book" },
    ...(user ? [{ to: "/bookings", label: t("nav.bookings"), icon: Ticket, testId: "nav-bookings" }] : []),
    { to: "/tournaments", label: t("nav.tournaments"), icon: Swords, testId: "nav-tournaments" },
    { to: "/coaching", label: t("nav.coaching"), icon: GraduationCap, testId: "nav-coaching" },
    { to: "/contact", label: t("nav.contact"), icon: Phone, testId: "nav-contact" },
  ];

  const settingsItems = (
    <>
      <DropdownMenuItem onClick={toggleTheme} className="gap-2 text-sm" data-testid="menu-theme-toggle">
        {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />} {theme === "dark" ? t("nav.light") : t("nav.dark")}
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="gap-2 text-sm" data-testid="menu-language"><Globe size={14} /> {t("nav.language")} · {languageNames[lang]}</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {Object.keys(languageNames).map(code => (
            <DropdownMenuItem key={code} onClick={() => setLang(code)} className="gap-2 text-sm" data-testid={`lang-${code}`}>
              <span className="text-xs font-mono w-5">{languageFlags[code]}</span>{languageNames[code]}{lang === code && <Check size={12} className="ml-auto text-coral" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid={TEST_IDS.NAV_HOME} className="flex items-center gap-2 group">
            <img src={LOGO_URL} alt="D&D Padel" className="h-12 w-12" />
            <span className="font-heading font-bold text-lg tracking-tight hidden sm:block text-foreground">Padel</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium tracking-wide transition-all duration-200 ${
                  isActive(link.to) ? "bg-coral/10 text-coral" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                <link.icon size={16} />
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-1">
            {user && user.role === "admin" && (
              <Link to="/admin" data-testid={TEST_IDS.NAV_ADMIN}>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 border-golden/30 text-golden hover:bg-golden/10">
                  <Shield size={14} /> {t("nav.admin")}
                </Button>
              </Link>
            )}

            {user && <NotificationBell />}

            {user ? (
              <div className="hidden md:flex items-center gap-6">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="rounded-full gap-1.5 pl-1" data-testid={TEST_IDS.NAV_PROFILE}>
                      <PlayerAvatar name={user.name} avatarUrl={user.avatar_url} size="xs" className="border-0" />
                      <span className="max-w-[100px] truncate">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild><Link to="/profile" className="gap-2" data-testid="menu-profile"><User size={14} /> {t("nav.profile")}</Link></DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {settingsItems}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="gap-2 text-destructive" data-testid={TEST_IDS.NAV_LOGOUT}><LogOut size={14} /> {t("nav.signout")}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full w-9 h-9" data-testid="settings-menu"><Settings size={16} /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">{settingsItems}</DropdownMenuContent>
                </DropdownMenu>
                <Link to="/login" data-testid={TEST_IDS.NAV_LOGIN}>
                  <Button className="rounded-full bg-coral hover:bg-coral-600 text-white px-5">{t("nav.signin")}</Button>
                </Link>
              </div>
            )}

            <Button variant="ghost" size="icon" className="md:hidden rounded-full" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map(link => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-${link.testId}`}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${isActive(link.to) ? "bg-coral/10 text-coral" : "text-muted-foreground"}`}>
                  <link.icon size={18} />
                  {link.label}
                </Link>
              ))}
              {!user && (
                <div className="flex items-center gap-2 px-4 py-2">
                  <Globe size={18} className="text-muted-foreground" />
                  {Object.keys(languageNames).map(code => (
                    <button key={code} onClick={() => setLang(code)} className={`px-3 py-1 rounded-full text-xs font-semibold ${lang === code ? "bg-coral text-white" : "bg-muted text-muted-foreground"}`} data-testid={`mobile-lang-${code}`}>{languageFlags[code]}</button>
                  ))}
                  <button onClick={toggleTheme} className="ml-auto p-2 rounded-full text-muted-foreground" data-testid="mobile-theme-toggle">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
                </div>
              )}
              {user ? (
                <>
                  <Link to="/profile" onClick={() => setMobileOpen(false)} className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${isActive("/profile") ? "bg-coral/10 text-coral" : "text-muted-foreground"}`} data-testid="mobile-nav-profile">
                    <User size={18} /> {t("nav.profile")}
                  </Link>
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-destructive w-full text-left">
                    <LogOut size={18} /> {t("nav.signout")}
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full rounded-xl bg-coral hover:bg-coral-600 text-white mt-2">{t("nav.signin")}</Button>
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
